import type { SlideProps } from "@/engine/app/offsite-config";

/** Punkte einer Gauß-Glocke für den SVG-Pfad (Breite 600, Höhe 150). */
function gaussPath(width = 600, height = 150, pad = 10): string {
  const pts: string[] = [];
  const n = 60;
  for (let i = 0; i <= n; i++) {
    const x = i / n; // 0..1
    const y = Math.exp(-Math.pow((x - 0.5) * 6, 2) / 2); // Glocke, σ auf Breite gemappt
    const px = pad + x * (width - 2 * pad);
    const py = height - pad - y * (height - 2 * pad);
    pts.push(`${i === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`);
  }
  return pts.join(" ");
}

export function Slide07LlmDenken(_: SlideProps) {
  const curve = gaussPath();
  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Wie ein Sprachmodell arbeitet</div>
        <h1 className="deck-title rise rise-2">
          Es rät das <br />
          <span className="accent">nächste Wort</span>
        </h1>
        <p className="deck-sub rise rise-3">
          Klingt banal — mit Milliarden Trainingstexten wird daraus verblüffend
          gutes Verstehen und Formulieren.
        </p>
      </header>

      <div className="deck-cols2" style={{ gap: 20 }}>
        <div className="col-card rise rise-4">
          <div className="col-eyebrow">Stärken</div>
          <h4>Wo es glänzt</h4>
          <p>
            Texte verstehen und zusammenfassen · Anträge und E-Mails vorformulieren
            · aus einer Beschreibung Software bauen · in Sekunden Varianten liefern.
          </p>
        </div>
        <div className="col-card rise rise-5">
          <div className="col-eyebrow">Grenzen</div>
          <h4>Wo du aufpassen musst</h4>
          <p>
            Es kann überzeugend klingen und trotzdem falsch liegen („halluzinieren“)
            · kennt keine SVO-Interna, wenn wir sie nicht mitgeben · Wissen endet am
            Trainingsstichtag · rechnet schlechter, als es schreibt.
          </p>
        </div>
      </div>

      <div
        className="rise rise-6"
        style={{
          margin: "1rem auto 0",
          display: "inline-flex",
          alignSelf: "center",
          padding: "0.5rem 1.2rem",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.25)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.85)",
          fontSize: "clamp(0.9rem, 1.3vw, 1.1rem)",
        }}
      >
        Probe aufs Exempel: „Der Himmel ist …?“
      </div>

      <div
        className="rise rise-6"
        style={{
          margin: "1.25rem auto 0",
          width: "min(680px, 70%)",
          textAlign: "center",
        }}
      >
        <svg
          viewBox="0 0 600 195"
          role="img"
          aria-label="Gaußsche Normalverteilung — Wahrscheinlichkeit des nächsten Worts"
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <defs>
            <linearGradient id="gaussFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--svo-blau, #008DF7)" stopOpacity="0.55" />
              <stop offset="100%" stopColor="var(--svo-blau, #008DF7)" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Beschriftung oben und unten AUSSERHALB der Kurve — nichts läuft rein. */}
          <text x="300" y="14" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="600">
            „blau“
          </text>
          <g transform="translate(0,26)">
            <path d={`${curve} L590,140 L10,140 Z`} fill="url(#gaussFill)" stroke="none" />
            <path d={curve} fill="none" stroke="var(--svo-blau, #008DF7)" strokeWidth="3" />
            <line x1="300" y1="140" x2="300" y2="-6" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeDasharray="4 5" />
            <line x1="10" y1="140" x2="590" y2="140" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
            {/* Plausible Nachbarn auf den Flanken — genau da entstehen die Fehler. */}
            <text x="182" y="66" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="13">
              „bewölkt“
            </text>
            <text x="418" y="66" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="13">
              „schön“
            </text>
            <text x="70" y="116" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="12">
              „grün“
            </text>
          </g>
          <text x="55" y="186" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="12">
            unwahrscheinlich
          </text>
          <text x="545" y="186" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="12">
            unwahrscheinlich
          </text>
        </svg>
        <div style={{ marginTop: 6, fontSize: "0.85rem", color: "rgba(255,255,255,0.6)" }}>
          Plausibel heißt nicht wahr — genau auf den Flanken entstehen die Fehler.
          Kontext („… trotz Sturmwarnung“) verschiebt die Kurve: Deshalb zählt, was ihr mitgebt.
        </div>
      </div>

      <footer className="deck-foot">
        <span>Deshalb gilt: Die KI liefert den Entwurf — den Haken dran machst du.</span>
      </footer>
    </div>
  );
}
